import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Loader2, Plus, Play, Image as ImageIcon, Film } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthConfig = () => {
  // Correctif B7 : le cookie httpOnly porte la session, plus d'en-tete Authorization.
  return {
    withCredentials: true,
    headers: {},
  };
};

/**
 * Composant pour upload MULTIPLE de photos/videos
 * Props:
 *   - value: array d'objets {url, type: 'image'|'video', public_id}
 *   - onChange(newArray): callback
 *   - maxItems: limite (par defaut 20)
 *   - allowVideo: autorise les videos (par defaut true)
 */
const MediaGallery = ({ value = [], onChange, maxItems = 20, allowVideo = true }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const items = Array.isArray(value) ? value : [];

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = maxItems - items.length;
    if (files.length > remaining) {
      toast.error(`Vous pouvez ajouter encore ${remaining} fichier(s) (max ${maxItems})`);
      return;
    }

    setUploading(true);
    const uploaded = [];
    let failed = 0;

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) { failed++; continue; }
      if (!allowVideo && isVideo) { failed++; continue; }

      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name} trop volumineux`);
        failed++;
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        const cfg = getAuthConfig();
        const endpoint = isVideo ? '/api/upload/video' : '/api/upload/image';
        const res = await axios.post(`${API_URL}${endpoint}`, formData, {
          ...cfg,
          headers: { ...cfg.headers, 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({
          url: res.data.url,
          public_id: res.data.public_id,
          type: isVideo ? 'video' : 'image',
        });
      } catch (err) {
        failed++;
      }
    }

    if (uploaded.length > 0) {
      onChange([...items, ...uploaded]);
      toast.success(`${uploaded.length} fichier(s) uploadé(s)`);
    }
    if (failed > 0) toast.error(`${failed} fichier(s) en échec`);

    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = async (index) => {
    const item = items[index];
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);

    // Optionnel : supprimer aussi de Cloudinary
    if (item.public_id) {
      try {
        await axios.delete(`${API_URL}/api/upload/${item.public_id}`, getAuthConfig());
      } catch (e) {
        // pas grave si échec, l'item est juste retiré du state
      }
    }
  };

  const accept = allowVideo ? 'image/*,video/*' : 'image/*';
  const canAdd = items.length < maxItems;

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} multiple onChange={handleFiles} className="hidden" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((item, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#E2E8F0] group bg-[#F8FAFC]">
            {item.type === 'video' ? (
              <>
                <video src={item.url} className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-5 h-5 text-[#0596DE] fill-[#0596DE] ml-0.5" />
                  </div>
                </div>
                <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded flex items-center gap-1">
                  <Film className="w-3 h-3" /> Vidéo
                </span>
              </>
            ) : (
              <img src={item.url} alt="" className="w-full h-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {canAdd && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-xl border-2 border-dashed border-[#E2E8F0] hover:border-[#0596DE] hover:bg-[#F0F9FF] transition flex flex-col items-center justify-center gap-1 text-[#64748B] hover:text-[#0596DE] disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs">Upload...</span>
              </>
            ) : (
              <>
                <Plus className="w-6 h-6" />
                <span className="text-xs font-medium">Ajouter</span>
                <span className="text-[10px]">{items.length}/{maxItems}</span>
              </>
            )}
          </button>
        )}
      </div>

      <p className="text-xs text-[#64748B] mt-3">
        {allowVideo ? 'Photos et vidéos' : 'Photos'} acceptées • Photos max 10 MB • Vidéos max 50 MB • Sélection multiple possible
      </p>
    </div>
  );
};

export default MediaGallery;
